import { useState, useEffect } from 'react'
import { useLocation } from 'wouter'
import { trackEvent } from '@/lib/analytics'

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSocialsOpen, setIsSocialsOpen] = useState(false)
  const [isMobileSocialsOpen, setIsMobileSocialsOpen] = useState(false)
  const [location] = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMenuOpen])

  const handleLinkClick = () => {
    setIsMenuOpen(false)
  }

  return (
    <>
      <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        isScrolled || isMenuOpen ? 'bg-black/90 backdrop-blur-sm' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <a href="/the-monsters-we-make" className="text-white hover:text-red-600 transition-colors">
              <span className="font-bebas text-2xl tracking-widest">TMWM</span>
            </a>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <a 
                href="/the-monsters-we-make" 
                className={`transition-colors font-poppins text-base tracking-wide hover:text-red-600 ${location === '/' ? 'text-red-600' : 'text-neutral-400'}`}
              >
                About The Monsters
              </a>
              <a
                href="/the-monsters-we-make/director-vision"
                className={`transition-colors font-poppins text-base tracking-wide hover:text-red-600 ${location === '/director-vision' ? 'text-red-600' : 'text-neutral-400'}`}
              >
                Director&apos;s Vision
              </a>
              <a
                href="/the-monsters-we-make/media"
                className={`transition-colors font-poppins text-base tracking-wide hover:text-red-600 ${location === '/media' ? 'text-red-600' : 'text-neutral-400'}`}
              >
                Media
              </a>
              <a 
                href="/the-monsters-we-make/get-in-touch" 
                onClick={() => trackEvent('contact_click', { site: 'the-monsters-we-make', film: 'the-monsters-we-make', placement: 'navigation', provider: 'internal' })}
                className={`transition-colors font-poppins text-base tracking-wide hover:text-red-600 ${location === '/get-in-touch' ? 'text-red-600' : 'text-neutral-400'}`}
              >
                Get In Touch
              </a>
              <a 
                href="/" 
                className="text-neutral-400 hover:text-red-600 transition-colors font-poppins text-base tracking-wide"
              >
                Filmabee Studios
              </a>
              <div className="relative">
                <button
                  onClick={() => setIsSocialsOpen(!isSocialsOpen)}
                  className="text-neutral-400 hover:text-red-600 transition-colors font-poppins text-base tracking-wide flex items-center gap-1"
                >
                  Socials
                  <svg className={`w-3.5 h-3.5 transition-transform duration-200 ${isSocialsOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isSocialsOpen && (
                  <div className="absolute top-full right-0 mt-2 w-44 bg-black/95 backdrop-blur-sm border border-neutral-800 shadow-xl">
                    <a
                      href="https://www.instagram.com/filmabeestudios/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-4 py-3 text-neutral-300 hover:text-red-600 hover:bg-white/5 transition-colors font-poppins text-sm"
                      onClick={() => { setIsSocialsOpen(false); trackEvent('social_click', { site: 'the-monsters-we-make', film: 'the-monsters-we-make', platform: 'instagram', placement: 'navigation' }) }}
                    >
                      Instagram
                    </a>
                    <a
                      href="https://filmabeestudios.substack.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-4 py-3 text-neutral-300 hover:text-red-600 hover:bg-white/5 transition-colors font-poppins text-sm"
                      onClick={() => { setIsSocialsOpen(false); trackEvent('social_click', { site: 'the-monsters-we-make', film: 'the-monsters-we-make', platform: 'substack', placement: 'navigation' }) }}
                    >
                      Substack
                    </a>
                    <a
                      href="https://www.imdb.com/title/tt44861862/?ref_=mv_close"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-4 py-3 text-neutral-300 hover:text-red-600 hover:bg-white/5 transition-colors font-poppins text-sm"
                      onClick={() => setIsSocialsOpen(false)}
                    >
                      IMDb
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Hamburger Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden flex flex-col justify-center items-center w-10 h-10 focus:outline-none"
              aria-label="Toggle menu"
            >
              <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-1.5' : ''}`}></span>
              <span className={`block w-6 h-0.5 bg-white mt-1.5 transition-all duration-300 ${isMenuOpen ? 'opacity-0' : ''}`}></span>
              <span className={`block w-6 h-0.5 bg-white mt-1.5 transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></span>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div className={`fixed inset-0 z-40 bg-black transition-all duration-500 md:hidden ${
        isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
      }`}>
        <div className="flex flex-col items-center justify-center h-full gap-8">
          <a 
            href="/the-monsters-we-make" 
            onClick={handleLinkClick}
            className={`transition-colors font-bebas text-4xl tracking-widest hover:text-red-600 ${location === '/' ? 'text-red-600' : 'text-white'}`}
          >
            About The Monsters
          </a>
          <a
            href="/the-monsters-we-make/director-vision"
            onClick={handleLinkClick}
            className={`transition-colors font-bebas text-4xl tracking-widest hover:text-red-600 ${location === '/director-vision' ? 'text-red-600' : 'text-white'}`}
          >
            Director&apos;s Vision
          </a>
          <a
            href="/the-monsters-we-make/media"
            onClick={handleLinkClick}
            className={`transition-colors font-bebas text-4xl tracking-widest hover:text-red-600 ${location === '/media' ? 'text-red-600' : 'text-white'}`}
          >
            Media
          </a>
          <a 
            href="/the-monsters-we-make/get-in-touch" 
             onClick={() => { handleLinkClick(); trackEvent('contact_click', { site: 'the-monsters-we-make', film: 'the-monsters-we-make', placement: 'mobile_navigation', provider: 'internal' }) }}
            className={`transition-colors font-bebas text-4xl tracking-widest hover:text-red-600 ${location === '/get-in-touch' ? 'text-red-600' : 'text-white'}`}
          >
            Get In Touch
          </a>
          <a 
            href="/" 
            onClick={handleLinkClick}
            className="text-white hover:text-red-600 transition-colors font-bebas text-4xl tracking-widest"
          >
            Filmabee Studios
          </a>
          <button
            onClick={() => setIsMobileSocialsOpen(!isMobileSocialsOpen)}
            className="text-white hover:text-red-600 transition-colors font-bebas text-4xl tracking-widest flex items-center gap-2"
          >
            Socials
            <svg className={`w-5 h-5 transition-transform duration-200 ${isMobileSocialsOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {isMobileSocialsOpen && (
            <div className="flex flex-col items-center gap-3">
              <a
                href="https://www.instagram.com/filmabeestudios/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => { handleLinkClick(); trackEvent('social_click', { site: 'the-monsters-we-make', film: 'the-monsters-we-make', platform: 'instagram', placement: 'mobile_navigation' }) }}
                className="text-white/80 hover:text-red-600 transition-colors font-bebas text-2xl tracking-widest"
              >
                Instagram
              </a>
              <a
                href="https://filmabeestudios.substack.com/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => { handleLinkClick(); trackEvent('social_click', { site: 'the-monsters-we-make', film: 'the-monsters-we-make', platform: 'substack', placement: 'mobile_navigation' }) }}
                className="text-white/80 hover:text-red-600 transition-colors font-bebas text-2xl tracking-widest"
              >
                Substack
              </a>
              <a
                href="https://www.imdb.com/title/tt44861862/?ref_=mv_close"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleLinkClick}
                className="text-white/80 hover:text-red-600 transition-colors font-bebas text-2xl tracking-widest"
              >
                IMDb
              </a>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default Navigation
